import { DateFormat, formatDate, PurchaseItemStatus } from "common";
import { Dayjs } from "dayjs";
import { SeriesOptionsType } from "highcharts";
import { sortBy } from "lodash";
import { CatalogItem, Experience, GuestStatus, LineItemType, Seller, UnitType } from "types";
import { api } from "../api/api";
import { config } from "../config";
import { AnalyticsMode } from "../stores/reports/analyticsFilterStore";

// Interface for the params
export interface DemographicsReportParams {
    seller: Seller;
    from: Dayjs;
    to: Dayjs;
    mode: AnalyticsMode;
    products: ID[];
    getName?: (key: any) => string | Promise<string>;
}

export const getDemographicsReport = async (params: DemographicsReportParams): Promise<SeriesOptionsType[]> => {
    const { seller } = params;
    const index = config.elasticsearch.indexes.purchase;

    if (!params.from.isValid() || !params.to.isValid()) {
        return getDemographicsResponse(null);
    }

    const { data } = await api.post(`/elasticsearch/${index}/_search?seller=${seller.id}`, getPayload(params));

    if (params.products.length === 1) {
        const { data: experience } = await api.get<Experience>(
            `/experiences/${params.products[0]}?seller=${seller.id}`,
        );
        const demographicsCatalogItems = experience.catalog.items.filter(
            (item) => item.unitType === UnitType.DEMOGRAPHIC,
        );
        return getDemographicsResponse(data, demographicsCatalogItems);
    }

    return getDemographicsResponse(data);
};

const getPayload = ({ seller, from, to, mode, products }: DemographicsReportParams) => {
    const filter = getBaseFilter(seller);

    const itemsFilter = filter.aggs.items.aggs.filtered;
    const itemsRange = itemsFilter.filter.bool.must;
    const query = filter.query.bool.must;
    const queryRange = query[1].nested.query.bool.must;

    const range = { from: formatDate(from, DateFormat.DATE_ISO), to: formatDate(to, DateFormat.DATE_ISO) };
    if (mode === AnalyticsMode.CASH_FLOW) {
        query.push({ range: { createdAt: range } });
    } else {
        const rangeFilter = { range: { "items.arrivalDate": range } };
        itemsRange.push(rangeFilter);
        queryRange.push(rangeFilter);
    }

    if (products.length > 0) {
        const experienceFilter = { terms: { "items.product.id": products } };
        itemsRange.push(experienceFilter);
        query.push({
            nested: {
                path: "items",
                query: experienceFilter,
            },
        });
    }

    return filter;
};

const getBaseFilter = (seller: Seller) => {
    return {
        size: 0,
        aggs: {
            items: {
                nested: { path: "items" },
                aggs: {
                    filtered: {
                        filter: {
                            bool: {
                                must: [{ term: { "items.status": PurchaseItemStatus.CONFIRMED } }],
                                must_not: { term: { "items.guestStatus": GuestStatus.NO_SHOW } },
                            },
                        },
                        aggs: {
                            pie_aggregation: {
                                nested: { path: "items.lineItems" },
                                aggs: {
                                    labels: {
                                        filter: {
                                            bool: {
                                                must: [{ term: { "items.lineItems.type": LineItemType.DEMOGRAPHIC } }],
                                            },
                                        },
                                        aggs: {
                                            demographics: {
                                                terms: {
                                                    field: "items.lineItems.name",
                                                    order: { quantity_count: "desc" },
                                                },
                                                aggs: {
                                                    quantity_count: { sum: { field: "items.lineItems.quantity" } },
                                                },
                                            },
                                        },
                                    },
                                },
                            },
                        },
                    },
                },
            },
        },
        query: {
            bool: {
                must: [
                    { term: { "seller.id": seller.id } },
                    { nested: { path: "items", query: { bool: { must: [] } } } },
                    {
                        nested: {
                            path: "items",
                            query: {
                                bool: {
                                    must: { term: { "items.status": PurchaseItemStatus.CONFIRMED } },
                                    must_not: { term: { "items.guestStatus": GuestStatus.NO_SHOW } },
                                },
                            },
                        },
                    },
                ],
            },
        },
    } as any;
};

const getDemographicsResponse = (data: any, catalogItems?: CatalogItem[]) => {
    if (!data) {
        return;
    }

    if (!data.aggregations) {
        console.error("Aggregations not found");
        return;
    }

    const demographicsResults = data.aggregations.items.filtered.pie_aggregation.labels.demographics.buckets;

    const results = catalogItems ? getSortedResults(demographicsResults, catalogItems) : demographicsResults;

    return results.map((r: any) => {
        return [r.key, Math.abs(r.quantity_count.value)];
    });
};

const getSortedResults = (results: Array<{ key: string }>, catalogItems?: CatalogItem[]) =>
    sortBy(
        results.map((result) => {
            const catalogItem = catalogItems?.find((item) => item.name === result.key);

            return {
                ...result,
                sequence: catalogItem?.sequence,
            };
        }),
        "sequence",
    );
